import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { api, ApiError } from '../lib/api.js';
import { LogoCropDialog } from './LogoCropDialog.js';

/**
 * fbs#99 - the practice logo, top-right of Settings → Practice info. Client-facing
 * forms (Form Builder) show it in place of the Credify mark.
 *
 * Picking a file opens the crop dialog (zoom + position, like the Provider Profile photo;
 * Chase 10-07). Saving it stores a 600 × 200-or-smaller image right away (PUT
 * /practice/logo), apart from the modal's Save, so the data URL stays small: forms embed it
 * in every page they render.
 */
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('unreadable'));
    i.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}

export function PracticeLogoField({ canEdit }: { canEdit: boolean }) {
  const [logo, setLogo] = useState<string | null>(null); // null while loading
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // The picked image, while the crop dialog is open (key remounts it for "Choose another").
  const [crop, setCrop] = useState<{ img: HTMLImageElement; key: number } | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<{ logo: string }>('/practice/logo')
      .then((r) => live && setLogo(r.logo || ''))
      .catch(() => live && setLogo(''));
    return () => {
      live = false;
    };
  }, []);

  const save = (next: string) => {
    setBusy(true);
    setMsg('');
    api
      .put<{ logo: string }>('/practice/logo', { logo: next })
      .then((r) => {
        setLogo(r.logo || '');
        setMsg(next ? 'Logo saved.' : 'Logo removed.');
      })
      .catch((e: unknown) => setMsg(e instanceof ApiError ? e.message : 'Could not save the logo.'))
      .finally(() => setBusy(false));
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    // The panes container marks the modal "unsaved" on any change; the logo saves on
    // its own, so keep this one out of it.
    e.stopPropagation();
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!LOGO_TYPES.includes(f.type)) {
      setMsg('Use a PNG, JPG or WebP image.');
      return;
    }
    setMsg('');
    loadImage(f)
      .then((img) => setCrop({ img, key: Date.now() }))
      .catch(() => setMsg('That image could not be read.'));
  };

  return (
    <div className="ds-logo">
      <div className={`ds-logo-box${logo ? '' : ' empty'}`}>
        {logo ? (
          <img src={logo} alt="Practice logo" />
        ) : (
          <span>{logo === null ? 'Loading…' : 'No logo'}</span>
        )}
      </div>
      <div className="ds-logo-side">
        <div className="ds-logo-title">Practice logo</div>
        <div className="ds-logo-help">
          Shown on client-facing forms. PNG, JPG or WebP. Zoom and position it, then it saves right
          away.
        </div>
        {canEdit ? (
          <div className="ds-logo-btns">
            <button
              type="button"
              className="btn sm"
              disabled={busy || logo === null}
              onClick={() => fileRef.current?.click()}
            >
              {logo ? 'Replace' : 'Upload logo'}
            </button>
            {logo && (
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => save('')}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className="ds-logo-help">Only admins and managers can change it.</div>
        )}
        {msg && (
          <div className="ds-logo-msg" role="status">
            {msg}
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={onPick}
      />
      {crop && (
        <LogoCropDialog
          key={crop.key}
          image={crop.img}
          onCancel={() => setCrop(null)}
          onChooseAnother={() => fileRef.current?.click()}
          onSave={(url) => {
            setCrop(null);
            save(url);
          }}
        />
      )}
    </div>
  );
}
